import styled, { keyframes } from 'styled-components';

/* ─── Brand tokens (shared with AddPaymentMethod) ─── */
const brand = {
  blue: '#0d4efe',
  teal: '#01ebb2',
  dark: '#0a1628',
  textPrimary: '#1a2340',
  textSecondary: '#5a6478',
  textMuted: '#8a93a6',
  border: '#e0e5f0',
  borderFocus: '#0d4efe',
  inputBg: '#f7f9fc',
  white: '#ffffff',
  danger: '#e74c3c',
  cardBg: '#ffffff',
};

/* ─── Status badge color map ─── */
const statusColors = {
  paid: { bg: 'rgba(1, 235, 178, 0.15)', color: '#0a6e3a', border: 'rgba(1, 235, 178, 0.5)' },
  open: { bg: 'rgba(13, 78, 254, 0.12)', color: '#0d4efe', border: 'rgba(13, 78, 254, 0.4)' },
  draft: { bg: 'rgba(138, 147, 166, 0.15)', color: '#5a6478', border: 'rgba(138, 147, 166, 0.4)' },
  void: { bg: 'rgba(231, 76, 60, 0.12)', color: '#b71c1c', border: 'rgba(231, 76, 60, 0.4)' },
  uncollectible: { bg: 'rgba(231, 76, 60, 0.12)', color: '#b71c1c', border: 'rgba(231, 76, 60, 0.4)' },
};

/* ─── Animations ─── */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Page wrapper (wider than the form card, holds the invoice list) ─── */
export const PageWrapper = styled.div`
  width: 100%;
  max-width: 860px;
  margin: 2rem auto;
  padding: 2.5rem 2.5rem 2rem;
  background: ${brand.cardBg};
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(10, 22, 40, 0.08);
  animation: ${fadeIn} 0.5s ease both;

  @media (max-width: 600px) {
    max-width: 100%;
    margin: 1rem;
    padding: 1.75rem 1.25rem 1.5rem;
  }
`;

/* ─── Back link ─── */
export const BackLink = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${brand.blue};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 1.25rem;
  transition: color 0.2s ease;

  &:hover {
    color: ${brand.teal};
  }
`;

/* ─── Header (title + subtitle) ─── */
export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

export const PageHeading = styled.h2`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: ${brand.textPrimary};
  margin: 0;
`;

export const SubHeading = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 0.88rem;
  color: ${brand.textMuted};
  margin: 0.35rem 0 0;
`;

/* ─── Invoice list ─── */
export const InvoiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

export const InvoiceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  padding: 1rem 1.25rem;
  border: 1px solid ${brand.border};
  border-radius: 12px;
  background: ${brand.inputBg};
  transition: all 0.2s ease;

  &:hover {
    background: ${brand.white};
    border-color: ${brand.blue};
    box-shadow: 0 2px 12px rgba(13, 78, 254, 0.08);
  }
`;

/* Left side — period + description */
export const InvoicePrimary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 240px;
  min-width: 0;
`;

export const PeriodLabel = styled.span`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.98rem;
  font-weight: 700;
  color: ${brand.textPrimary};
`;

export const DescriptionLabel = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 0.8rem;
  color: ${brand.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* Right side — amount, status, action */
export const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const Amount = styled.span`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.98rem;
  font-weight: 700;
  color: ${brand.textPrimary};
  min-width: 72px;
  text-align: right;
`;

export const StatusBadge = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;

  ${(props) => {
    const c = statusColors[props.$status] || statusColors.draft;
    return `
      background: ${c.bg};
      color: ${c.color};
      border: 1px solid ${c.border};
    `;
  }}
`;

export const ActionLink = styled.a`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  color: ${brand.blue};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 12px;
  border: 1px solid ${brand.border};
  border-radius: 8px;
  background: ${brand.white};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${brand.blue};
    color: ${brand.teal};
    box-shadow: 0 2px 8px rgba(13, 78, 254, 0.12);
  }
`;

/* ─── Empty / error state ─── */
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
  padding: 3rem 1.5rem;
  margin-top: 1.5rem;
  border: 1px dashed ${brand.border};
  border-radius: 12px;
  color: ${brand.textSecondary};

  p {
    font-family: 'Roboto', sans-serif;
    font-size: 0.92rem;
    margin: 0;
  }
`;